import React from 'react';
import { Button } from 'primereact/button';
import Tooltip from 'components/controls/Tooltip';
import keyIcon from 'images/v2/icons/svg/fill/svg/key.svg';
import styled from 'styled-components';
import { RoleModel } from 'services/EpdClient';

type Props = {
  id: string;
  name: string;
  roles: RoleModel[];
  isUserAccountManager: boolean;
  isUserVerifierInHomeCompany: boolean;
  onLeave: (companyId: string, companyName: string, roles: RoleModel[], isUserVerifierInHomeCompany: boolean) => void;
};

const CompanyItem: React.FC<Props> = ({ id, name, roles, isUserAccountManager, isUserVerifierInHomeCompany, onLeave }) => {
  const isLeaveOrganisationButtonDisabled = roles.length === 1 && isUserVerifierInHomeCompany;
  return (
    <Container>
      <KeyIconAndCompanyNameContainer>
        {isUserAccountManager && (
          <KeyIconContainer>
            <KeyIcon src={keyIcon} alt="key" />
            <Tooltip position={'mouse'}>User is account manager</Tooltip>
          </KeyIconContainer>
        )}
        <CompanyName>{name}</CompanyName>
      </KeyIconAndCompanyNameContainer>

      <RoleNamesAndLeaveActionContainer>
        {isUserAccountManager ? (
          <RoleNamesForAccountManager>{roles.map((r) => r.name).join(', ')}</RoleNamesForAccountManager>
        ) : (
          <>
            <RoleNames>{roles.map((r) => r.name).join(', ')}</RoleNames>
            <Delimiter />
            <div>
              <LeaveButton
                label="Leave organisation"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onLeave(id, name, roles, isUserVerifierInHomeCompany);
                }}
                disabled={isLeaveOrganisationButtonDisabled}
              />
              {isLeaveOrganisationButtonDisabled && (
                <Tooltip position={'mouse'}>
                  EPD Verifiers cannot leave their organisation. Please contact support for changes.
                </Tooltip>
              )}
            </div>
          </>
        )}
      </RoleNamesAndLeaveActionContainer>
    </Container>
  );
};

export default CompanyItem;

const Container = styled.div`
  width: 100%;
  min-height: 48px;
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 8px;
`;

const KeyIconAndCompanyNameContainer = styled.div`
  margin-left: 16px;
  display: flex;
  align-items: center;
`;

const KeyIconContainer = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 20px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(p) => p.theme.colors.statusLabelGreenBg};
`;

const KeyIcon = styled.img`
  width: 15px;
  height: 15px;
`;

const CompanyName = styled.span`
  ${(props) => props.theme.typography.bodyInput}
  color: ${(p) => p.theme.colors.primaryBlack};
`;

const RoleNamesAndLeaveActionContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
`;

const RoleNamesForAccountManager = styled.span`
  ${(props) => props.theme.typography.bodyInput}
  font-weight: 500;
  height: 22px;
  color: ${(p) => p.theme.colors.primaryGreen};
  background-color: ${(p) => p.theme.colors.statusLabelGreenBg};
  padding: 4px 10px 4px 10px;
  font-size: 12px;
  text-transform: uppercase;
  border-radius: 2px;
`;

const RoleNames = styled.span`
  ${(props) => props.theme.typography.bodyInput}
  color: ${(p) => p.theme.colors.primaryBlack};
`;

const Delimiter = styled.div`
  width: 1px;
  height: 16px;
  background-color: ${(p) => p.theme.colors.elementsInputBorder};
  margin-left: 11px;
  margin-right: 11px;
`;

const LeaveButton = styled(Button)`
  width: 153px;
  height: 22px;
  border-radius: 2px;

  background-color: ${(p) => p.theme.colors.primaryOrange};
  border-color: ${(p) => p.theme.colors.primaryOrange};
  font-size: 14px;
  font-weight: 500;

  display: flex;
  justify-content: center;

  &&:hover {
    background-color: ${(p) => p.theme.colors.lightOrange};
    border-color: ${(p) => p.theme.colors.lightOrange};
  }

  &:disabled {
    background-color: ${(p) => p.theme.colors.elementsButtonDisabled};
    border-color: ${(p) => p.theme.colors.elementsButtonDisabled};
  }
`;
